import { SingleAssetUsage, BoundingBox, Point } from '@/store/modules/asset'
import { AppModule } from '@/store/modules/app'
import { ErrorPayload, ErrorsModule } from '@/store/modules/error'
import { ToolbarModule } from '@/store/modules/toolbar'
import { FramePuzzleModule } from '@/store/modules/framePuzzle'
import i18n from '@/i18n'
import { ImagePosition } from '@/rest'

export class FrameClipper {

    private _imageSize: [number, number] = [0, 0]
    private _center: [number, number] = [0, 0]

    private _zoom: number
    private _rotation: number
    private _minScale: number = 1.0
    private _maxScale: number = 1.0
    private _error: boolean = false

    private static _umlauf: number = 5
    private static _minPpi: number = 43.271

    private constructor(
        public displaySize: [number, number],
        public minSize: [number, number],
        public displayOffset: [number, number],
        private targetSize: [number, number] | undefined,
        private _usageName?: string,
        private changedCenter?: (center: [number, number]) => void,
        private changedError?: (hasError: boolean) => void) {
        this._zoom = 0.0
        this._rotation = 0.0
    }

    public static getCollageClipper(active: boolean, width: number, height: number, stepIdx?: number, usageName?: string): FrameClipper {
        let step = AppModule.steps[stepIdx || AppModule.currentStepIndex]
        let clipRects = step.clipRects
        let usage = usageName
        if (clipRects) {
            // init targetSize and minSize
            let targetWidth = 0
            let targetHeight = 0
            let minWidth = 0
            let minHeight = 0
            let canvasWidthInMm = 0
            let canvasHeightInMm = 0
            let ratio = FramePuzzleModule.imageRatios.get(FramePuzzleModule.parts)
            if (ratio && ratio.width && ratio.height) {
                canvasWidthInMm = ratio.width
                canvasHeightInMm = ratio.height
            }
            targetWidth = canvasWidthInMm
            targetHeight = canvasHeightInMm
            if (targetWidth && targetHeight) {
                minWidth = Math.round(this._minPpi * targetWidth / 25.4)
                minHeight = Math.round(this._minPpi * targetHeight / 25.4)
            }
            if (active) {
                return new FrameClipper([width, height], [minWidth, minHeight], clipRects[2], [targetWidth, targetHeight],
                    usage,
                    (center) => ToolbarModule.SetCenter(center),
                    (hasError) => {
                        if (usage) {
                            ToolbarModule.SetToolbarError(hasError)
                            let error: ErrorPayload = { key: usage }
                            if (hasError) {
                                ToolbarModule.SetErrorKey(usage)
                                let assetName = ""
                                if (usageName && FramePuzzleModule.activeAssets[usageName]) {
                                    assetName = FramePuzzleModule.activeAssets[usageName].name
                                }
                                error.message = i18n.t('selectedImage').toString() + assetName + i18n.t('tooSmall').toString()
                                    + i18n.t('pleaseChooseAnother').toString() + minWidth + 'x' + minHeight + i18n.t('pxSize').toString()
                            }
                            ErrorsModule.SetError(error)
                        }
                    })
            } else {
                return new FrameClipper([width, height], [minWidth, minHeight], clipRects[2], [targetWidth, targetHeight])
            }
        }
        throw new Error('no image clipper defined for current step: ' + AppModule.currentStepIndex)
    }

    public initializeUsage(usage: SingleAssetUsage, size: [number, number]) {
        this._error = false
        this._imageSize = size
        this._center = [size[0] / 2.0, size[1] / 2.0]
        this._rotation = 0
        this.rotate(usage.rotation)
        this._zoom = usage.zoom
        this.adjustScaleRange()
        if (usage.center[0] > 0 && usage.center[1] > 0) {
            this._center = [...usage.center] as [number, number]
        }
        this.adjustCenter()
    }

    public reset() {
        this._error = false
    }

    get imageSize(): [number, number] {
        return this._imageSize
    }

    get scale(): number {
        return this._minScale + (this._maxScale - this._minScale) * (this._zoom / 100.0)
    }

    get rotation(): number {
        return this._rotation
    }

    get translateX(): number {
        let scale = this.scale
        let width = this._imageSize[0]
        let translateX = (this.displaySize[0] / (2.0 * scale)) - this._center[0]
        switch (this._rotation) {
            case 90:
            case 180:
                return translateX + width
        }
        return translateX
    }

    get translateY(): number {
        let scale = this.scale
        let height = this._imageSize[1]
        let translateY = (this.displaySize[1] / (2.0 * scale)) - this._center[1]
        switch (this._rotation) {
            case 270:
            case 180:
                return translateY + height
        }
        return translateY
    }

    // origin is upper left
    get boundingBox(): BoundingBox {
        let scale = this.scale
        let centerDiffX = this.displaySize[0] / (2.0 * scale)
        let centerDiffY = this.displaySize[1] / (2.0 * scale)
        let llx = Math.round(this._center[0] - centerDiffX)
        let urx = Math.round(this._center[0] + centerDiffX)
        let lly = Math.round(this._center[1] - centerDiffY)
        let ury = Math.round(this._center[1] + centerDiffY)
        let ll: Point = [llx, lly]
        let ur: Point = [urx, ury]
        return { ll, ur }
    }

    set zoom(zoom: number) {
        this._zoom = zoom
        this.adjustCenter()
    }

    get zoom() {
        return this._zoom
    }

    set rotation(rotation: number) {
        this.rotate(rotation)
        this.adjustScaleRange()
        this.adjustCenter()
        if (!this.adjustCenter() && this.changedCenter) {
            this.changedCenter([this._center[0], this._center[1]])
        }
    }

    move(offset: [number, number]) {
        let scale = this.scale
        this.adjustCenter([this._center[0] - (offset[0] / scale), this._center[1] - (offset[1] / scale)])
    }

    get transformation(): string {
        return 'transform: scale('
            + this.scale + ') translateX('
            + this.translateX + 'px) translateY('
            + this.translateY + 'px) rotate('
            + this.rotation + 'deg)'
    }

    get usageName(): string | undefined {
        return this._usageName
    }

    private rotate(rotation: number) {
        let rotationDiff = rotation - this._rotation
        if (rotationDiff < 0) {
            rotationDiff += 360
        }
        switch (rotationDiff) {
            case 90:
                this._imageSize = [this._imageSize[1], this._imageSize[0]]
                this._center = [this._imageSize[0] - this._center[1], this._center[0]]
                break
            case 180:
                this._center = [this._imageSize[0] - this._center[0], this._imageSize[1] - this._center[1]]
                break
            case 270:
                this._imageSize = [this._imageSize[1], this._imageSize[0]]
                this._center = [this._center[1], this._imageSize[1] - this._center[0]]
                break
        }
        this._rotation = rotation
    }

    private adjustCenter(newCenter?: [number, number]): boolean {
        let border = this.border
        let scale = this.scale
        let minx = (this.displaySize[0] / (2.0 * scale)) + border
        let miny = (this.displaySize[1] / (2.0 * scale)) + border
        let changed = newCenter !== undefined
        let cx = newCenter ? newCenter[0] : this._center[0]
        let cy = newCenter ? newCenter[1] : this._center[1]
        if (cx < minx) {
            cx = minx
            changed = true
        } else if (cx > this._imageSize[0] - minx) {
            cx = this._imageSize[0] - minx
            changed = true
        }
        if (cy < miny) {
            cy = miny
            changed = true
        } else if (cy > this._imageSize[1] - miny) {
            cy = this._imageSize[1] - miny
            changed = true
        }
        if (changed) {
            this._center = [cx, cy]
            if (this.changedCenter) {
                this.changedCenter([cx, cy])
                return true
            }
        }
        return false
    }

    private adjustScaleRange() {
        let border = this.border
        this._minScale = Math.max(this.displaySize[0] / (this._imageSize[0] - border * 2), this.displaySize[1] / (this._imageSize[1] - border * 2))
        this._maxScale = this.maxZoom()
        if (this._minScale > this._maxScale) {
            this._maxScale = this._minScale
            //if (!this._error) {
            this._error = true
            if (this.changedError) {
                this.changedError(this._error)
            }
            //}
        } else {
            if (this._error) {
                this._error = false
                if (this.changedError) {
                    this.changedError(this._error)
                }
            }
        }
    }

    get border(): number {
        return 0
    }

    private maxZoom(): number {
        if (this.targetSize) {
            var targetWidth = this.targetSize[0]
            var targetHeight = this.targetSize[1]
            targetWidth = targetWidth - FrameClipper._umlauf
            targetHeight = targetHeight - FrameClipper._umlauf
            var targetLongSide = Math.max(targetWidth, targetHeight)
            var minActualLongSide = (targetLongSide / 25.4) * FrameClipper._minPpi
            return Math.max(this.displaySize[0], this.displaySize[1]) / minActualLongSide
        }
        return Math.min(this.displaySize[0] / this.minSize[0], this.displaySize[1] / this.minSize[1])
    }

}